import * as React from "react";
import { Box, Button, Chip } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import type {
  GridColDef,
  GridFilterItem,
  GridFilterModel,
} from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { getOperatorMessageId } from "./filterOperators";
import {
  formatFilterValue,
  resolveFilterLabel,
  type ValueOption,
} from "./filterFormat";

type ColumnMeta = {
  headerName: string;
  type: GridColDef["type"];
  valueOptions?: readonly ValueOption[];
};

export type ActiveFilterChipsProps = {
  filterModel: GridFilterModel;
  columns: GridColDef[];
  onRemove: (field: string) => void;
  onClearAll: () => void;
};

/** Whether a single filter item actually constrains rows (mirrors muiGridFilterModel). */
function isActiveItem(item: GridFilterItem): boolean {
  if (item.operator === "isEmpty" || item.operator === "isNotEmpty") return true;
  if (item.operator === "isAnyOf") {
    return Array.isArray(item.value) && item.value.length > 0;
  }
  const v = item.value;
  return v !== undefined && v !== null && v !== "";
}

function ActiveFilterChipsComponent({
  filterModel,
  columns,
  onRemove,
  onClearAll,
}: ActiveFilterChipsProps) {
  const { t } = useTranslation();

  // Defer so rapid typing (localModel updates every keystroke) doesn't thrash the bar.
  const deferredModel = React.useDeferredValue(filterModel);

  const columnMap = React.useMemo(() => {
    const map = new Map<string, ColumnMeta>();
    for (const col of columns) {
      if (!col.field) continue;
      const withOptions = col as GridColDef & {
        valueOptions?: readonly ValueOption[];
      };
      map.set(col.field, {
        headerName:
          typeof col.headerName === "string" && col.headerName
            ? col.headerName
            : col.field,
        type: col.type,
        valueOptions: withOptions.valueOptions,
      });
    }
    return map;
  }, [columns]);

  const activeItems = (deferredModel.items ?? []).filter(isActiveItem);

  if (activeItems.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="active-filter-chips"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        mb: 1,
        borderRadius: 1.5,
        border: "1px solid rgba(253, 149, 79, 0.25)",
        background: "linear-gradient(135deg, #ffffff 0%, #fff7ef 100%)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mr: 0.5,
          color: "#d9772f",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <FilterAltOutlinedIcon fontSize="small" />
        <span>{t(messages.Filter_active_title())}</span>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        {activeItems.map((item) => {
          const meta = columnMap.get(item.field);
          const headerName = meta?.headerName ?? item.field;
          const operatorLabel = resolveFilterLabel(
            t,
            getOperatorMessageId(meta?.type, item.operator),
          );
          const valueText = formatFilterValue(t, {
            type: meta?.type,
            operator: item.operator,
            value: item.value,
            valueOptions: meta?.valueOptions,
          });

          return (
            <Chip
              key={item.field}
              size="small"
              variant="outlined"
              onDelete={() => onRemove(item.field)}
              deleteIcon={
                <CloseIcon
                  titleAccess={`${t(messages.Filter_remove())}: ${headerName}`}
                />
              }
              sx={{
                maxWidth: 280,
                backgroundColor: "#fff",
                borderColor: "rgba(253, 149, 79, 0.5)",
                "& .MuiChip-label": { fontSize: 12 },
                "& .MuiChip-deleteIcon": {
                  color: "rgba(0, 0, 0, 0.4)",
                  "&:hover": { color: "#d9772f" },
                },
              }}
              label={
                <span>
                  <strong>{headerName}</strong>{" "}
                  <span style={{ color: "#d9772f" }}>{operatorLabel}</span>
                  {valueText ? <span> {valueText}</span> : null}
                </span>
              }
            />
          );
        })}
      </Box>

      <Button
        size="small"
        onClick={onClearAll}
        startIcon={<CloseIcon fontSize="small" />}
        sx={{
          flex: "0 0 auto",
          textTransform: "none",
          fontSize: 12,
          color: "#d9772f",
          "&:hover": { backgroundColor: "rgba(253, 149, 79, 0.08)" },
        }}
      >
        {`${t(messages.Filter_clear_all())} (${activeItems.length})`}
      </Button>
    </Box>
  );
}

export const ActiveFilterChips = React.memo(ActiveFilterChipsComponent);
